/**
 * Context React per la gestione del tema light/dark/system.
 *
 * Il provider applica il tema risolvato su <html>:
 * - classe "dark" per Tailwind (darkMode: 'class')
 * - attributo data-theme per le CSS variables in styles.css
 *
 * Persiste la scelta in localStorage con chiave "floraltech-theme".
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  /** Tema scelto dall'utente (light | dark | system). */
  theme: Theme;
  /** Tema effettivo applicato (light | dark). */
  resolved: 'light' | 'dark';
  /** Imposta un nuovo tema. */
  setTheme: (t: Theme) => void;
}

const STORAGE_KEY = 'floraltech-theme';

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'system',
  resolved: 'light',
  setTheme: () => {},
});

/** Risolve il tema system in light/dark. */
const resolve = (theme: Theme): 'light' | 'dark' => {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
};

/** Legge il tema salvato da localStorage. */
const readStored = (): Theme => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch { /* noop */ }
  return 'system';
};

/** Applica il tema risolvato al DOM. */
const applyToDOM = (resolved: 'light' | 'dark'): void => {
  const html = document.documentElement;
  html.setAttribute('data-theme', resolved);
  if (resolved === 'dark') {
    html.classList.add('dark');
  } else {
    html.classList.remove('dark');
  }
};

const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(readStored);
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolve(readStored()));

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    const r = resolve(t);
    setResolved(r);
    applyToDOM(r);
    try { localStorage.setItem(STORAGE_KEY, t); } catch { /* noop */ }
  }, []);

  /* Applica il tema al mount. */
  useEffect(() => {
    applyToDOM(resolve(theme));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Ascolta cambi preferenza sistema se tema = system. */
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      const r = e.matches ? 'dark' : 'light';
      setResolved(r);
      applyToDOM(r);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

/** Hook per accedere al tema corrente. */
const useTheme = (): ThemeContextValue => useContext(ThemeContext);

export { ThemeProvider, useTheme };
export type { Theme };
